import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, WritableSignal, inject, signal } from '@angular/core';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject, debounceTime } from 'rxjs';
import { TeleportDirective } from '@ngneat/overview';

import { IconComponent } from '@atoms/icon/icon.component';
import { TitleComponent } from '@atoms/title/title.component';
import { LayoutService } from '@services/layout.service';
import { ScrollbarService } from '@services/scrollbar.service';
import { SsrService } from '@services/ssr.service';

@Component({
  selector: 'app-dropdown-main',
  imports: [NgClass, NgStyle, NgTemplateOutlet, TeleportDirective, IconComponent, TitleComponent],
  templateUrl: './dropdown-main.component.html',
  styleUrls: ['./dropdown-main.component.scss'],
  host: { ngSkipHydration: 'true' },
})
export class DropdownMainComponent implements AfterViewInit, OnChanges, OnDestroy {
  private el = inject(ElementRef);
  private layoutService = inject(LayoutService);
  private scrollbarService = inject(ScrollbarService);
  private ssrService = inject(SsrService);
  @Input() title: string = '';
  @Input() size: 'default' | 'small' = 'default';
  @Input() align: 'left' | 'center' | 'right' = 'left';
  @Input() active: boolean = false;
  @Input() position: 'relative' | 'absolute' = 'relative';
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('dropdownEl') dropdownEl?: ElementRef;
  public top: WritableSignal<number | undefined> = signal(undefined);
  public left: WritableSignal<number | undefined> = signal(undefined);
  public width: WritableSignal<number | undefined> = signal(undefined);
  public direction: 'top' | 'bottom' = 'bottom';
  public documentWheel: Subject<boolean | undefined> = new Subject<boolean | undefined>();

  @HostListener('window:resize') onResize(): void {
    if (!this.active) return;
    setTimeout(() => {
      this.toggleScroll();
      this.setPosition();
      this.setDirection();
    });
  }

  @HostListener('window:scroll') onScroll(): void {
    if (!this.active) return;
    this.setPosition();
  }

  @HostListener('document:wheel', ['$event.target']) onScroll2(): void {
    if (this.active && this.position === 'absolute') this.documentWheel.next(true);
  }

  close(): void {
    this.layoutService.enableScroll('dropdown');
    this.closed.emit();
  }

  toggleScroll(): void {
    if (this.ssrService.isServer()) return;
    if (this.active && window.innerWidth < 1000) this.layoutService.disableScroll('dropdown');
    else this.layoutService.enableScroll('dropdown');
  }

  setPosition(): void {
    if (!this.active || this.ssrService.isServer()) return;
    const parentEl: HTMLElement = this.el.nativeElement.parentElement;
    const parentWidth: number = parentEl.getBoundingClientRect().width;
    this.width.set(parentWidth);

    if (this.position !== 'absolute') return;
    const parentTop: number = parentEl.getBoundingClientRect().top;
    const parentLeft: number = parentEl.getBoundingClientRect().left;
    this.top.set(parentTop + parentEl.offsetHeight + 8);
    this.left.set(parentLeft);
  }

  setDirection(): void {
    if (this.ssrService.isServer()) return;
    const windowHeight: number = window.innerHeight;
    const headerHeight: number = 112;
    const positionFromTop: number = this.el?.nativeElement.closest('app-dropdown').getBoundingClientRect().top - headerHeight;
    const positionFromBottom: number = windowHeight - this.el?.nativeElement.closest('app-dropdown').getBoundingClientRect().top;
    let maxHeight: number = windowHeight - headerHeight - 148;
    if (maxHeight > 500) maxHeight = 500;

    if (positionFromBottom > 640) {
      this.direction = 'bottom';
    } else {
      this.direction = positionFromTop > positionFromBottom && positionFromTop > maxHeight ? 'top' : 'bottom';
    }
  }

  constructor() {
    this.documentWheel.pipe(debounceTime(200), takeUntilDestroyed()).subscribe((value: boolean | undefined) => {
      if (!this.active) return;
      if (value !== undefined) this.setPosition();
    });
    this.scrollbarService.value$.pipe(takeUntilDestroyed()).subscribe((value: { id: string; value: number }) => {
      if (!this.active) return;
      this.setPosition();
    });
  }

  ngAfterViewInit(): void {
    if (this.ssrService.isBrowser()) {
      setTimeout(() => this.dropdownEl?.nativeElement.scroll({ top: 0 }));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && !changes['active']['firstChange'] && changes['active']['currentValue'] && this.position === 'relative' && this.ssrService.isBrowser()) this.setDirection();
    this.toggleScroll();
    this.setPosition();
  }

  ngOnDestroy(): void {
    this.layoutService.enableScroll('dropdown');
  }
}
